import { IGlobalAPIResponse } from "@/src/types/globalData";
import { rtkQuerieSetup } from "../services/rtkQuerieSetup";
import { IAllProductsListAPIResponse, IAllProductsListQueryParams } from "@/src/types/allProducts";
import { IAllBrandsListAPIResponse } from "@/src/types/allBrands";
import { IAllCategoriesListAPIResponse } from "@/src/types/allCategories";
import { IAllFiltersListAPIResponse } from "@/src/types/allFilters";
import { IUserCartAPIResponse } from "@/src/types/cart";
import { IUserAllAddressesAPIResponse } from "@/src/types/allAddress";
import { IMostSearchedAPIResponse } from "@/src/types/mostSearched";
import { ISearchSuggestionsAPIResponse, ISearchSuggestionsQueryParams } from "@/src/types/searchSuggestions";
import type { INewArrivalsAPIResponse } from "@/src/types/newArrivals";
import { IUserWishlistAPIResponse } from "@/src/types/wishlist";
import { IUserProfileAPIResponse } from "@/src/types/userProfile";
import { IUserOrdersAPIResponse, IUserOrdersQueryParams } from "@/src/types/myOrders";
import { ISingleOrderDetailsAPIResponse } from "@/src/types/singleOrder";
import type { IFAQAPIResponse } from "@/src/types/faqs";
import { IRecentlyViewedProductAPIResponse } from "@/src/types/recentlyViewedProduct";


export const clientSideGetApis = rtkQuerieSetup.injectEndpoints({
    endpoints: (builder) => ({
        /** get global settings */
        getGlobalSettings: builder.query<IGlobalAPIResponse, void>({
            query: () => ({
                url: `/user/get-global`,
                method: 'GET',
            }),
            providesTags: ['GlobalData'],
        }),
        /** get products list with filters */
        getAllProductsData: builder.query<IAllProductsListAPIResponse, IAllProductsListQueryParams>({
            query: (params) => ({
                url: `/user/products`,
                method: 'GET',
                params,
            }),
        }),
        /** get all brands data */
        getAllBrandsData: builder.query<IAllBrandsListAPIResponse, void>({
            query: () => ({
                url: `/user/brands`,
                method: 'GET',
            }),
        }),
        /** get all categories data */
        getAllCategoriesData: builder.query<IAllCategoriesListAPIResponse, { category?: string }>({
            query: ({ category }) => ({
                url: `/user/categories`,
                method: 'GET',
                params: category ? { category } : undefined,
            }),
        }),
        /** get all filters data */
        getAllFiltersData: builder.query<IAllFiltersListAPIResponse, void>({
            query: () => ({
                url: `/user/products/filters`,
                method: 'GET',
            }),
        }),
        /** get user cart data */
        getUserCartData: builder.query<IUserCartAPIResponse, void>({
            query: () => ({
                url: `/user/cart`,
                method: 'GET',
            }),
            providesTags: ['Cart'],
        }),
        /** get user wishlist data */
        getUserWishlistData: builder.query<IUserWishlistAPIResponse, void>({
            query: () => ({
                url: `/user/wishlist`,
                method: 'GET',
            }),
            providesTags: ['Wishlist'],
        }),
        /** get user all addresses data */
        getUserAllAddressesData: builder.query<IUserAllAddressesAPIResponse, void>({
            query: () => ({
                url: `/user/all-addresses`,
                method: 'GET',
            }),
            providesTags: ['Addresses'],
        }),
        /** get search products data */
        getSearchProductsData: builder.query<IAllProductsListAPIResponse, IAllProductsListQueryParams>({
            query: (params) => ({
                url: `/user/search-products`,
                method: 'GET',
                params,
            }),
        }),
        /** get search suggestions data */
        getSearchSuggestionsData: builder.query<ISearchSuggestionsAPIResponse, ISearchSuggestionsQueryParams>({
            query: (params) => ({
                url: `/user/search-suggestions`,
                method: 'GET',
                params,
            }),
        }),
        /** get most searched products data */
        getMostSearchedData: builder.query<IMostSearchedAPIResponse, void>({
            query: () => ({
                url: `/user/most-searched-products`,
                method: 'GET',
            }),
        }),
        /** get suggested products (search panel sidebar) */
        getSuggestedProductsData: builder.query<INewArrivalsAPIResponse, void>({
            query: () => ({
                url: `/user/suggested-products`,
                method: 'GET',
            }),
        }),
        /** get recently viewed products */
        getRecentlyViewedProductsData: builder.query<IRecentlyViewedProductAPIResponse, void>({
            query: () => ({
                url: `/user/recently-viewed`,
                method: 'GET',
            }),
            providesTags: ['RecentlyViewed'],
        }),
        /** get user profile data */
        getUserProfileData: builder.query<IUserProfileAPIResponse, void>({
            query: () => ({
                url: `/user/get-user-profile`,
                method: 'GET',
            }),
            providesTags: ['UserProfile'],
        }),
        /** get user orders data */
        getUserOrdersData: builder.query<IUserOrdersAPIResponse, IUserOrdersQueryParams>({
            query: (params) => ({
                url: `/user/all-order`,
                method: 'GET',
                params,
            }),
            providesTags: ['MyOrders'],
        }),
        /** get single order details data */
        getSingleOrderDetailsData: builder.query<ISingleOrderDetailsAPIResponse, string>({
            query: (order_id) => ({
                url: `/user/order-details/${order_id}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, order_id) => [{ type: 'MyOrders', id: order_id }],
        }),
        /** download order invoice as PDF blob */
        getOrderInvoiceData: builder.query<Blob, string>({
            query: (order_id) => ({
                url: `/user/order-invoice/${order_id}`,
                method: 'GET',
                responseHandler: (response) => response.blob(),
            }),
        }),
        /** FAQs list (`type` matches `/user/faqs?type=` — e.g. membership, contact, about) */
        getFAQData: builder.query<IFAQAPIResponse, { type?: string }>({
            query: ({ type }) => ({
                url: `/user/faqs`,
                method: 'GET',
                params: { type },
            }),
        }),
    }),
});

export const {
    useGetGlobalSettingsQuery,
    useGetAllProductsDataQuery,
    useGetAllBrandsDataQuery,
    useGetAllCategoriesDataQuery,
    useGetAllFiltersDataQuery,
    useGetUserCartDataQuery,
    useGetUserWishlistDataQuery,
    useGetUserAllAddressesDataQuery,
    useGetMostSearchedDataQuery,
    useGetSearchProductsDataQuery,
    useGetSearchSuggestionsDataQuery,
    useGetSuggestedProductsDataQuery,
    useGetRecentlyViewedProductsDataQuery,
    useGetUserProfileDataQuery,
    useGetUserOrdersDataQuery,
    useGetSingleOrderDetailsDataQuery,
    useLazyGetOrderInvoiceDataQuery,
    useGetFAQDataQuery,
} = clientSideGetApis;
